import { ExclamationCircleOutlined } from '@ant-design/icons'
import { ResidentStatus } from '@lmc/ts-common'
import {
  Button,
  EmptyState,
  FormCard,
  FormItem,
  NoDataIcon,
  SelectAddOption,
  theme
} from '@lmc/ui-kit'
import type { Option } from '@lmc/ui-kit/src/dataEntry/MultiSelect/MultiSelect'
import { Text } from '@lmc/ui-kit/src/general'
import { Select, Tooltip } from 'antd'
import { getNominative } from 'common/utils'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useGroupedResidents } from 'hooks/useGroupedOptions'
import useHomeType from 'hooks/useHomeType'
import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import useResidentsList from 'screens/Rostering/hooks/useResidentsList'
import { getFundingSourceName, getRequiredRule } from 'screens/Rostering/utils'
import { styled } from 'styled-components'
import { commonPatterns } from 'utils/enhancedTestIds'
import { getNewAddress } from '../utils'
import AddressFormItems from './AddressFormItems'

type FundingType = {
  id: string
  name: string
  hours: number
}

type ResidentFormCardProps = {
  testId?: string
  form: any
  home: any
  onResidentChange?: (residentId: string) => void
  onFundingChange?: (fundingId: string) => void
  setFormData: (data: any) => void
  formData: any
  isSubmitting?: boolean
  prefilledResidentId?: string
}

const ResidentFormCard: FC<ResidentFormCardProps> = ({
  testId,
  form,
  home,
  onResidentChange,
  onFundingChange,
  setFormData,
  formData,
  isSubmitting = false,
  prefilledResidentId
}) => {
  const { t } = useTranslation()
  const baseId = testId || 'resident-form-card'
  const [fundingValue, setFundingValue] = useState<FundingType | undefined>(
    undefined
  )
  const [showValidation, setShowValidation] = useState(false)
  const { residents, refetchResidents } = useResidentsList({
    filters: { statuses: [ResidentStatus.active] }
  })
  const { groupedResidentOptions } = useGroupedResidents(residents)
  const { hasRota } = useHomeType()
  const flags = useFeatureFlags({ flagNames: ['geofencing'] })?.data
  const geofencingEnabled = flags?.geofencing?.enabled
  const isGeofencingEnabled =
    (hasRota ?? false) &&
    (home?.rotaSettings as { geofencing?: boolean } | undefined)?.geofencing ===
      true &&
    geofencingEnabled === true

  const preferredPeopleNominative = home?.preferredPeopleNominative
  const selectedResident = form.getFieldValue('subjectId')

  const isAddressInvalidForGeofencing = (address: {
    notValidForGeofencing?: boolean
    lat?: number | null
    lng?: number | null
  }) =>
    isGeofencingEnabled &&
    (address.notValidForGeofencing === true ||
      address.lat == null ||
      address.lng == null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    refetchResidents()
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (prefilledResidentId && !formData?.subjectId) {
      form?.setFieldValue('subjectId', prefilledResidentId)
      onResidentChange?.(prefilledResidentId)
    }
  }, [prefilledResidentId])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isSubmitting) {
      setShowValidation(true)
    }
  }, [isSubmitting])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (fundingValue) {
      setShowValidation(false)
    }
  }, [fundingValue])

  const foundResident = residents?.find(
    (resident) => resident?.id === selectedResident
  )

  const hasValidFundingSources =
    foundResident?.carePackage?.fundingSources instanceof Array

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form?.isFieldsTouched()) {
      setFormData((prev: any) => ({ ...prev, fundingTypeId: undefined }))
      form?.setFieldValue('addressId', undefined)
    }
    if (fundingTypeOptions?.length === 1) {
      setFormData((prev: any) => ({
        ...prev,
        fundingTypeId: fundingTypeOptions[0].value.id
      }))
    }
  }, [selectedResident, form])

  const fundingTypeOptions = hasValidFundingSources
    ? foundResident?.carePackage?.fundingSources?.map((source) => {
        const name = getFundingSourceName(source, home) || source.type
        const fundingObj: FundingType = {
          id: source.type,
          name,
          hours: source.hoursPerWeek || 0
        }
        return {
          value: fundingObj,
          label: name
        }
      }) || []
    : []

  const addressOptions = hasValidFundingSources
    ? foundResident?.addresses?.map((address) => {
        const addressString = getNewAddress(address, true)
        const invalidForGeofencing = isAddressInvalidForGeofencing(address)
        return {
          // @ts-ignore
          value: `${address?._id || address?.suburb || address?.name}`,
          searchLabel: addressString,
          label: (
            <AddressCellWrapper>
              {invalidForGeofencing && (
                <Tooltip
                  placement="top"
                  title={
                    <Text>{t('ADDRESS_NOT_VALID_FOR_GEOFENCING_TOOLTIP')}</Text>
                  }
                  overlayInnerStyle={{
                    width: '350px',
                    padding: '8px 16px'
                  }}
                >
                  <GeofencingWarningIcon />
                </Tooltip>
              )}
              <Text>{addressString}</Text>
            </AddressCellWrapper>
          )
        }
      }) || []
    : []

  const filterOption = (input: string, option?: Option<string>) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (fundingTypeOptions.length > 0 && !fundingValue) {
      const firstOption = fundingTypeOptions[0].value
      setFundingValue(firstOption)
      setFormData((prev: any) => ({
        ...prev,
        fundingTypeId: firstOption.id
      }))

      if (onFundingChange) {
        onFundingChange(firstOption.id)
      }
    }
  }, [fundingTypeOptions, fundingValue, form, onFundingChange])

  const handleResidentChange = (residentId: string) => {
    setFundingValue(undefined)

    if (onResidentChange) {
      onResidentChange(residentId)
    }
  }

  const handleFundingChange = (value: FundingType) => {
    setFundingValue(value)

    const isNewFundingSource = !fundingTypeOptions.some(
      (option) =>
        option.value.id === value.id && option.value.name === value.name
    )

    if (isNewFundingSource) {
      setFormData((prev: any) => ({
        ...prev,
        newFundingSource: {
          name: value.name,
          hours: value.hours
        },
        fundingTypeId: undefined
      }))

      if (onFundingChange) {
        onFundingChange('')
      }
    } else if (value && value.id) {
      setFormData((prev: any) => ({
        ...prev,
        fundingTypeId: value.id,
        newFundingSource: undefined
      }))

      if (onFundingChange) {
        onFundingChange(value.id)
      }
    } else {
      setFormData((prev: any) => ({
        ...prev,
        fundingTypeId: undefined,
        newFundingSource: undefined
      }))

      if (onFundingChange) {
        onFundingChange('')
      }
    }
  }

  return (
    <FormCard level={2} padding="16px" data-testid={baseId}>
      <FormItem
        data-testid={commonPatterns.field(baseId, 'resident')}
        name="subjectId"
        initialValue={prefilledResidentId}
        label={
          <Text>
            {getNominative(preferredPeopleNominative, 'Resident') ||
              t('SERVICE_USER')}
          </Text>
        }
        rules={[
          getRequiredRule(
            getNominative(preferredPeopleNominative, 'Resident') ||
              t('SERVICE_USER')
          )
        ]}
      >
        <Select
          data-testid={commonPatterns.input(baseId, 'resident')}
          allowClear={!prefilledResidentId}
          optionFilterProp="children"
          filterOption={filterOption}
          showSearch
          disabled={!!prefilledResidentId}
          onChange={handleResidentChange}
          notFoundContent={
            <EmptyState
              containerStyle={{ paddingTop: 24 }}
              title={
                <EmptyStateWrapper>
                  <Text>
                    {t('SELECT_EMPTY_STATE_RESIDENT', {
                      variable: getNominative(
                        preferredPeopleNominative,
                        'Resident'
                      )
                    })}
                  </Text>
                </EmptyStateWrapper>
              }
              description={
                <Link to="/residents/list" target="_blank" rel="noreferrer">
                  <Button variant="primary">{t('ADD')}</Button>
                </Link>
              }
              icon={
                <NoDataIconWrapper>
                  <NoDataIcon />
                </NoDataIconWrapper>
              }
            />
          }
        >
          {groupedResidentOptions &&
            groupedResidentOptions.map(
              (group: { label: string; options: any[] }) => (
                <Select.OptGroup key={group.label} label={group.label}>
                  {group.options.map(
                    (option: {
                      value: string
                      label: string
                      image: React.ReactNode
                    }) => (
                      <Select.Option
                        key={String(option.value)}
                        value={option.value}
                        label={option.label}
                      >
                        <OptionContainer>
                          <ImageContainer>{option.image} </ImageContainer>
                          <StyledText>{option.label}</StyledText>
                        </OptionContainer>
                      </Select.Option>
                    )
                  )}
                </Select.OptGroup>
              )
            )}
        </Select>
      </FormItem>

      <FormItem
        data-testid={commonPatterns.field(baseId, 'funding-type')}
        label={
          <MandatoryText>
            <RedText>*</RedText>
            <Text>{t('FUNDING_TYPE')}</Text>
          </MandatoryText>
        }
        rules={[getRequiredRule(t('FUNDING_TYPE'))]}
        validateStatus={showValidation && !fundingValue ? 'error' : ''}
        help={
          showValidation && !fundingValue
            ? t('FUNDING_TYPE') + ' is required'
            : ''
        }
      >
        <SelectAddOption<FundingType>
          data-testid={commonPatterns.input(baseId, 'funding-type')}
          options={fundingTypeOptions}
          selectedValue={fundingValue}
          setSelectedValue={handleFundingChange}
          valueKey="id"
          fields={[
            {
              key: 'name',
              placeholder: t('NAME'),
              type: 'text',
              size: 60
            },
            {
              key: 'hours',
              placeholder: t('HOURS'),
              type: 'number',
              size: 40
            }
          ]}
        />
      </FormItem>

      <FormItem name="fundingTypeId" hidden>
        <input type="hidden" />
      </FormItem>

      <AddressFormItems
        testId={`${baseId}-address`}
        addressOptions={addressOptions || []}
        selectedResident={selectedResident}
        preferredPeopleNominative={preferredPeopleNominative}
        formData={formData}
        form={form}
        setFormData={setFormData}
        defaultFirstAddress={true}
      />
    </FormCard>
  )
}

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
`

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const NoDataIconWrapper = styled.div`
  scale: 0.5;
  height: 50px;
  display: flex;
  align-items: center;
`

const StyledText = styled(Text)<{ isCapitalized?: boolean }>`
  text-transform: ${(props) => (props.isCapitalized ? 'capitalized' : 'none')};
  color: ${(props) => props.theme.color.neutral.color3};
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const MandatoryText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const RedText = styled.span`
  color: red;
`

const AddressCellWrapper = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const GeofencingWarningIcon = styled(ExclamationCircleOutlined)`
  color: ${(props) => props.theme.color.status.warning};
  font-size: 16px;
  flex-shrink: 0;
`

export default ResidentFormCard
